import { Modal, TextInput, Stack, Button, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useUpdateDevice, DeviceUpdatePayload } from '@/hooks/useDevices';

interface DeviceEditModalProps {
  opened: boolean;
  onClose: () => void;
  device: {
    id: string;
    hostname: string;
    ipAddress: string | null;
    primaryMacAddress: string | null;
  } | null;
}

export function DeviceEditModal({ opened, onClose, device }: DeviceEditModalProps) {
  const updateDevice = useUpdateDevice();

  const form = useForm<DeviceUpdatePayload>({
    initialValues: {
      hostname: device?.hostname || '',
      ipAddress: device?.ipAddress || '',
      primaryMacAddress: device?.primaryMacAddress || '',
    },
  });

  // Reset form when device changes
  if (device && form.values.hostname !== device.hostname && !form.isDirty()) {
    form.setValues({
      hostname: device.hostname || '',
      ipAddress: device.ipAddress || '',
      primaryMacAddress: device.primaryMacAddress || '',
    });
  }

  const handleSubmit = (values: DeviceUpdatePayload) => {
    if (!device) return;

    updateDevice.mutate(
      { deviceId: device.id, data: values },
      {
        onSuccess: (response) => {
          notifications.show({
            title: 'Device Updated',
            message: `Updated: ${response.updatedFields.join(', ')}`,
            color: 'green',
          });
          onClose();
        },
        onError: (error) => {
          notifications.show({
            title: 'Update Failed',
            message: error instanceof Error ? error.message : 'Failed to update device',
            color: 'red',
          });
        },
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit Device: ${device?.hostname || ''}`}
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Hostname"
            placeholder="Enter hostname"
            {...form.getInputProps('hostname')}
          />
          <TextInput
            label="IP Address"
            placeholder="Enter IP address (e.g., 192.168.1.100)"
            {...form.getInputProps('ipAddress')}
          />
          <TextInput
            label="MAC Address"
            placeholder="Enter MAC address (e.g., AA:BB:CC:DD:EE:FF)"
            {...form.getInputProps('primaryMacAddress')}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={updateDevice.isPending}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
